import Button from 'ext-ui/components/Button/Button'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { useTranslate } from 'src/localization/Trns'

import SvgAddToWallet from '../icons/SvgAddToWallet'
import SvgAdjustLimit from '../icons/SvgAdjustLimit'
import SvgDots from '../icons/SvgDots'
import SvgReplaceCard from '../icons/SvgReplaceCard'
import SvgReportFraud from '../icons/SvgReportFraud'
import SvgReportLostOrStolen from '../icons/SvgReportLostOrStolen'
import SvgTrash from '../icons/SvgTrash'
import * as style from './CardActionsMenu.style'

/**
 * The menu trigger's `data-testid`. Both the full hero and the pinned compact bar
 * mount this menu, so the id matches twice in the document; a locator that needs
 * one of them scopes through the owning hero's container test id
 * (`card-detail-hero` / `card-hero-compact`).
 */
const TRIGGER_TEST_ID = 'card-actions-trigger'

/**
 * Per-action capability verdicts.
 *
 * Every key except `adjustLimit` FAILS CLOSED when omitted. `addToWallet` joined
 * that set in EX-40599: it provisions live credentials to a device, so an
 * unresolved answer must hide it, never show it. `adjustLimit` stays
 * visible-unless-`false` because it only navigates — the destination tile does
 * its own permission work, so an over-eager link costs nothing.
 */
export type CardActionCapabilities = {
  adjustLimit?: boolean
  addToWallet?: boolean
  replaceCard?: boolean
  reportLostOrStolen?: boolean
  reportFraud?: boolean
  cancelCard?: boolean
}

export type CardLifecycleMenuAction =
  | 'replaceCard'
  | 'reportLostOrStolen'
  | 'reportFraud'
  | 'cancelCard'

export type CardActionsMenuProps = {
  capabilities?: CardActionCapabilities
  onAdjustLimit?: () => void
  onAddToWallet?: () => void
  onReplaceCard?: () => void
  onReportLostOrStolen?: () => void
  onReportFraud?: () => void
  onCancelCard?: () => void
  busyActions?: CardLifecycleMenuAction[]
}

type MenuItem = {
  key: keyof CardActionCapabilities
  /** Items sharing a group render together; a separator divides groups. */
  group: number
  title: string
  subtext?: string
  icon: ReactElement
  severe?: boolean
  disabled?: boolean
  onSelect: () => void
}

/**
 * The hero's ••• actions menu (EX-40352). A menu-button: the trigger opens a
 * `role="menu"` of `menuitem`s with arrow-key navigation, Escape/outside-click
 * dismissal, and focus returned to the trigger on keyboard close.
 *
 * Every item is capability-gated and invokes a page-owned handler: the lifecycle
 * four go through the shared lifecycle controller, Adjust limit deep links to
 * the Spend Limit tile, and Add to Wallet opens the wallet modal (EX-40599).
 */
const CardActionsMenu: React.FC<CardActionsMenuProps> = ({
  capabilities,
  onAdjustLimit,
  onAddToWallet,
  onReplaceCard,
  onReportLostOrStolen,
  onReportFraud,
  onCancelCard,
  busyActions,
}): ReactElement => {
  const translate = useTranslate()
  // Per-instance id (review finding #10): the full hero and the pinned compact
  // bar both mount this menu, so a module-constant id would collide. `useId`
  // gives each instance a unique, stable id for the trigger's `aria-controls` →
  // `role="menu"` link.
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  // Which end to focus when the menu opens: ArrowUp opens on the LAST item, every
  // other opener (ArrowDown / click / Enter / Space) on the first — the WAI-ARIA
  // menu-button pattern. A ref, not state, so setting it doesn't force a render.
  const focusLastOnOpenRef = useRef(false)

  // Focus the trigger by querying it out of the container rather than holding a
  // ref on it: a forwarded ref on the ext-ui Button forces its legacy fallback
  // (`ButtonLegacy`), so the trigger would render on a different design-system
  // channel than the ref-free Show details / Freeze buttons beside it. Query the
  // semantic `aria-haspopup="menu"` (the trigger is the only such node in the
  // container) rather than its `data-testid`, so focus return — a real a11y
  // behavior — never depends on a test attribute surviving the build.
  const closeMenu = useCallback((restoreFocus: boolean): void => {
    setOpen(false)
    if (restoreFocus) {
      containerRef.current
        ?.querySelector<HTMLButtonElement>('[aria-haspopup="menu"]')
        ?.focus()
    }
  }, [])

  const selectAction = (handler?: () => void): void => {
    closeMenu(true)
    handler?.()
  }

  const allItems: MenuItem[] = [
    {
      key: 'adjustLimit',
      group: 0,
      title: translate('Adjust limit'),
      subtext: translate("Change this card's spend limit"),
      icon: <SvgAdjustLimit />,
      onSelect: () => selectAction(onAdjustLimit),
    },
    {
      key: 'addToWallet',
      group: 0,
      title: translate('Add to Wallet'),
      subtext: translate('Apple Wallet or Google Wallet'),
      icon: <SvgAddToWallet />,
      onSelect: () => selectAction(onAddToWallet),
    },
    {
      key: 'replaceCard',
      group: 1,
      title: translate('Replace card'),
      subtext: translate(
        'Request a replacement and lock this card while your bank reviews it.',
      ),
      icon: <SvgReplaceCard />,
      disabled: busyActions?.includes('replaceCard'),
      onSelect: () => selectAction(onReplaceCard),
    },
    {
      key: 'reportLostOrStolen',
      group: 2,
      title: translate('Report lost or stolen'),
      subtext: translate(
        'Report the card and lock it while your bank reviews it.',
      ),
      icon: <SvgReportLostOrStolen />,
      severe: true,
      disabled: busyActions?.includes('reportLostOrStolen'),
      onSelect: () => selectAction(onReportLostOrStolen),
    },
    {
      key: 'reportFraud',
      group: 2,
      title: translate('Report fraud'),
      subtext: translate('Flag unauthorized charges for issuer review'),
      icon: <SvgReportFraud />,
      severe: true,
      disabled: busyActions?.includes('reportFraud'),
      onSelect: () => selectAction(onReportFraud),
    },
    {
      key: 'cancelCard',
      group: 2,
      title: translate('Cancel physical card'),
      subtext: translate('Permanently close this card and its virtual cards'),
      icon: <SvgTrash />,
      severe: true,
      disabled: busyActions?.includes('cancelCard'),
      onSelect: () => selectAction(onCancelCard),
    },
  ]
  // Everything but Adjust limit fails closed — see `CardActionCapabilities`.
  const failClosedKeys: Array<keyof CardActionCapabilities> = [
    'addToWallet',
    'replaceCard',
    'reportLostOrStolen',
    'reportFraud',
    'cancelCard',
  ]
  const visibleItems = allItems.filter((item) =>
    failClosedKeys.includes(item.key)
      ? capabilities?.[item.key] === true
      : capabilities?.[item.key] !== false,
  )

  // Move focus into the menu when it opens — the first item by default, the last
  // when opened with ArrowUp. Items carry a roving `tabIndex={-1}`, so focus is
  // moved programmatically here (and by the arrow-key handler), never by Tab.
  useEffect(() => {
    if (!open) return
    // Read and clear the ArrowUp intent BEFORE the empty-menu early return. If a
    // menu with no visible items (every `capabilities` entry withheld) were opened
    // with ArrowUp, leaving the ref set would make the NEXT open — by click, Enter,
    // or ArrowDown — land on the last item instead of the first.
    const focusLast = focusLastOnOpenRef.current
    focusLastOnOpenRef.current = false
    const items = menuRef.current?.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])',
    )
    if (!items || items.length === 0) return
    const target = focusLast ? items[items.length - 1] : items[0]
    target?.focus()
  }, [open])

  // Outside mouse click closes (a click on a non-focusable area won't blur the
  // menu, so this is needed in addition to the focus-out handler below).
  useEffect(() => {
    if (!open) return undefined
    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node
      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('click', onDocumentClick)
    return () => document.removeEventListener('click', onDocumentClick)
  }, [open])

  const focusableItems = (): HTMLElement[] =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ) ?? [],
    )

  // Attached to each menuitem (interactive elements), not the role="menu"
  // container, so arrow-key navigation works without a handler on a static div.
  const onMenuKeyDown = (event: React.KeyboardEvent): void => {
    const items = focusableItems()
    if (items.length === 0) return
    const index = items.indexOf(document.activeElement as HTMLElement)
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        items[(index + 1) % items.length]?.focus()
        break
      case 'ArrowUp':
        event.preventDefault()
        items[(index - 1 + items.length) % items.length]?.focus()
        break
      case 'Home':
        event.preventDefault()
        items[0]?.focus()
        break
      case 'End':
        event.preventDefault()
        items[items.length - 1]?.focus()
        break
      case 'Escape':
        event.preventDefault()
        // Stop the keydown before it reaches the document-level Escape listener
        // in useCardRevealDisclosure — otherwise dismissing this menu also
        // collapses an open Show details panel in the same subtree.
        event.stopPropagation()
        closeMenu(true)
        break
      default:
        break
    }
  }

  const onTriggerKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      focusLastOnOpenRef.current = event.key === 'ArrowUp'
      setOpen(true)
    }
  }

  // Close (no focus restore) when focus leaves the whole cluster — e.g. Tab-out.
  // Guard on a real relatedTarget: focus moving *into* the menu on open reports a
  // null relatedTarget (jsdom and some browsers), which must not read as leaving.
  const onBlur = (event: React.FocusEvent): void => {
    const next = event.relatedTarget as Node | null
    if (next && containerRef.current && !containerRef.current.contains(next)) {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} css={style.root} onBlur={onBlur}>
      <Button
        variant="secondary"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={translate('More actions')}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onTriggerKeyDown}
        data-testid={TRIGGER_TEST_ID}
      >
        <SvgDots width={16} height={16} />
      </Button>
      {open && (
        <div css={style.menuAnchor}>
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={translate('More actions')}
            css={style.menu}
          >
            {visibleItems.map((item, index) => {
              const previous = visibleItems[index - 1]
              const needsSeparator =
                previous !== undefined && previous.group !== item.group
              return (
                <React.Fragment key={item.key}>
                  {needsSeparator && (
                    <div role="separator" css={style.separator} />
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    css={item.severe ? style.menuItemSevere : style.menuItem}
                    onClick={() => {
                      if (item.disabled) {
                        closeMenu(true)
                        return
                      }
                      item.onSelect()
                    }}
                    onKeyDown={onMenuKeyDown}
                    aria-disabled={item.disabled || undefined}
                    data-disabled={item.disabled || undefined}
                    data-testid={`card-action-${item.key}`}
                  >
                    <span css={style.itemIcon}>{item.icon}</span>
                    <span css={style.itemText}>
                      <span css={style.itemTitle}>{item.title}</span>
                      {item.subtext && (
                        <span css={style.itemSubtext}>{item.subtext}</span>
                      )}
                    </span>
                  </button>
                </React.Fragment>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default CardActionsMenu
